/**
 * @file AdminAppointments.jsx — /cx/citas
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/admin";
import { ADMIN_STRINGS, APPOINTMENT_STRINGS } from "../constants/strings";
import { ALL_STATUSES } from "../../../shared/appointment-status.js";
import {
  Card,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "../ui";
import "./AdminGeneric.css";

const STATUS_TONES = {
  AGENDADA: "warn",
  CONFIRMADA: "ok",
  COMPLETADA: "primary",
  CANCELADA: "neutral",
  NO_ASISTIO: "err",
};

export default function AdminAppointments() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await adminService.getAppointments(params);
      setAppointments(res.data || []);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="ag-page">
      <header className="ag-head">
        <span className="ag-eyebrow">Admin · Citas</span>
        <h1 className="ag-title">{ADMIN_STRINGS.APPOINTMENTS_TITLE}</h1>
      </header>

      <div className="ag-filters">
        <Select
          label="Estado"
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}
          placeholder="Todos"
        >
          <SelectItem value="__all__">Todos</SelectItem>
          {ALL_STATUSES.map((s) => (
            <SelectItem key={s} value={s}>
              {APPOINTMENT_STRINGS?.STATUS_LABELS?.[s] || s}
            </SelectItem>
          ))}
        </Select>
        <Input type="date" label="Desde" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <Input type="date" label="Hasta" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      {isLoading ? (
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      ) : appointments.length === 0 ? (
        <Card className="ag-empty">
          <p>{ADMIN_STRINGS.APPOINTMENTS_EMPTY}</p>
        </Card>
      ) : (
        <>
          <p className="ag-count">
            {appointments.length} {appointments.length === 1 ? "resultado" : "resultados"}
          </p>
          <Card padded={false} className="ag-table-wrap">
            <Table>
              <THead>
                <TR>
                  <TH>Fecha</TH>
                  <TH>Profesional</TH>
                  <TH>Paciente</TH>
                  <TH>Estado</TH>
                  <TH>Motivo</TH>
                </TR>
              </THead>
              <TBody>
                {appointments.map((a) => (
                  <TR
                    key={a.id}
                    onClick={() => navigate(`/cita/${a.id}`)}
                    className="ag-row-clickable"
                  >
                    <TD>
                      <span className="ag-date">
                        {new Date(a.dateTime).toLocaleString("es-MX", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                    </TD>
                    <TD>
                      {a.professional?.user?.firstName} {a.professional?.user?.lastName}
                    </TD>
                    <TD>
                      {a.patient?.user?.firstName} {a.patient?.user?.lastName}
                    </TD>
                    <TD>
                      <Chip tone={STATUS_TONES[a.status] || "neutral"} size="sm">
                        {APPOINTMENT_STRINGS?.STATUS_LABELS?.[a.status] || a.status}
                      </Chip>
                    </TD>
                    <TD>
                      <span className="ag-truncate">{a.reason || "—"}</span>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
